// ClientListView.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Button, TextField, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Popover, CssBaseline, useMediaQuery, List, ListItem, ListItemText, Grid, Accordion, AccordionSummary, AccordionDetails, Tooltip, Tabs, Tab, Checkbox, FormControlLabel, Badge, Chip, FormControl, InputLabel, Select, MenuItem, InputAdornment } from '@mui/material';
import { ConfigProvider } from 'antd';
import {
  Building2, 
  UserPlus, 
  Edit, 
  Trash2, 
  MapPin, 
  Search, 
  X, 
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Mail,
  Phone,
  Star,
  AlertCircle,
  Copy,
  CheckCircle2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Building,
  Users,
  Link,
  Link2,
  Globe,
  Map,
  CheckCircle
} from 'lucide-react';
import { useReactTable, getCoreRowModel, flexRender, getSortedRowModel } from '@tanstack/react-table';
import api from "../../../authentication/axiosInstance";
import debounce from 'lodash/debounce';
import SiteContactsManager from '../../../components/SiteContactsManager';
import styles from './ClientsListView.module.css';
import { Select as AntSelect, DatePicker, InputNumber } from 'antd';
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete';
import moment from 'moment';

const ClientListView = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deletePopoverId, setDeletePopoverId] = useState(null);
  const [selectedClientSites, setSelectedClientSites] = useState([]);
  const [isSitesModalOpen, setIsSitesModalOpen] = useState(false);
  const [sitesSearchText, setSitesSearchText] = useState('');
  const [clientContacts, setClientContacts] = useState([]);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState([]);
  const [copiedField, setCopiedField] = useState(null);
  const [copiedTooltip, setCopiedTooltip] = useState({ show: false, text: '', x: 0, y: 0 });
  const [contactsFilter, setContactsFilter] = useState('all');
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [contactTabValue, setContactTabValue] = useState(0);
  const [clientSites, setClientSites] = useState([]);
  const [clientSitesLoading, setClientSitesLoading] = useState(false);
  const [siteContactAssignments, setSiteContactAssignments] = useState({});
  const [selectedContactForSites, setSelectedContactForSites] = useState(null);
  const [isSiteAssignmentModalOpen, setIsSiteAssignmentModalOpen] = useState(false);
  const [siteContactMatrix, setSiteContactMatrix] = useState({});
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [expandedViewTab, setExpandedViewTab] = useState(0);
  const [isSiteModalOpen, setIsSiteModalOpen] = useState(false);
  const [siteForm, setSiteForm] = useState({});
  const [territoryManagers, setTerritoryManagers] = useState([]);
  const [staffList, setStaffList] = useState([]);

  const isMobile = useMediaQuery('(max-width:768px)');

  const fetchClients = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/clients/');
      // Fetch contacts and sites counts for each client
      const clientsWithCounts = await Promise.all(
        response.data.map(async (client) => {
          const [contactsRes, sitesRes] = await Promise.all([
            api.get(`/clients/${client.client_id}/contacts/`),
            api.get(`/clients/${client.client_id}/sites/`)
          ]);
          return {
            ...client,
            contacts: contactsRes.data,
            sites: sitesRes.data
          };
        })
      );
      setClients(clientsWithCounts);
      setFilteredClients(clientsWithCounts);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchTerritoryManagers = async () => {
    try {
      const response = await api.get('/staff/territory_manager_staff/');
      setTerritoryManagers(response.data);
    } catch (error) {
      console.error('Failed to fetch Territory Managers:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await api.get('/staff/');
      setStaffList(response.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchTerritoryManagers();
    fetchStaff();
  }, [fetchClients]);

  const handleSearch = useCallback((value) => {
    setSearchText(value);
    const filtered = clients.filter((client) =>
      (client.client_name?.toLowerCase() || '').includes(value.toLowerCase()) ||
      `${client.primary_contact?.first_name || ''} ${client.primary_contact?.last_name || ''}`.toLowerCase().includes(value.toLowerCase()) ||
      (client.primary_contact?.email?.toLowerCase() || '').includes(value.toLowerCase())
    );
    setFilteredClients(filtered);
  }, [clients]);

  const debouncedSearch = useMemo(() => debounce(handleSearch, 300), [handleSearch]);

  const showModal = (client = null) => {
    setIsEditMode(!!client);
    setEditingClient(client || {
      client_name: '',
      contact_first_name: '',
      contact_last_name: '',
      contact_phone: '',
      contact_email: '',
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (isEditMode) {
        await api.put(`/clients/${editingClient.client_id}/`, editingClient);
      } else {
        await api.post('/clients/', editingClient);
      }
      handleCloseModal();
      fetchClients();
    } catch (error) {
      console.error('Failed to submit client:', error);
    }
  };

  const handleDelete = async (clientId) => {
    try {
      await api.delete(`/clients/${clientId}/`);
      fetchClients();
    } catch (error) {
      console.error('Failed to delete client:', error);
    }
    setDeletePopoverId(null);
  };

  const fetchClientSites = async (clientId, forContactsModal = false) => {
    try {
      setClientSitesLoading(forContactsModal);
      const response = await api.get(`/clients/${clientId}/sites/`);
      
      if (forContactsModal) {
        setClientSites(response.data);
        setClientSitesLoading(false);
      } else {
        setSelectedClientSites(response.data);
        setIsSitesModalOpen(true);
        setSitesSearchText('');
      }
    } catch (error) {
      console.error('Failed to fetch client sites:', error);
      setClientSitesLoading(false);
    }
  };

  const handleCloseSitesModal = () => {
    setIsSitesModalOpen(false);
    setSelectedClientSites([]);
    setSitesSearchText('');
  };

  const handleSearchInputChange = (e) => {
    setSearchInputValue(e.target.value);
  };

  const debouncedSetSitesSearch = useMemo(
    () => debounce((value) => {
      setSitesSearchText(value);
    }, 300),
    []
  );

  const filteredSites = useMemo(() => {
    const searchValue = searchInputValue.toLowerCase();
    if (!searchValue) return selectedClientSites;
    
    return selectedClientSites.filter(
      site => site.site_name.toLowerCase().includes(searchValue) || 
             site.region.toLowerCase().includes(searchValue)
    );
  }, [searchInputValue, selectedClientSites]);

  const fetchClientContacts = async (clientId) => {
    try {
      const response = await api.get(`/clients/${clientId}/contacts/`);
      setClientContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch client contacts:', error);
    }
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingContact.contact_id) {
        await api.put(`/contacts/${editingContact.contact_id}/`, editingContact);
      } else {
        const contactData = {
          ...editingContact,
          make_primary: !clientContacts.length
        };
        await api.post(`/clients/${editingClient.client_id}/contacts/`, contactData);
      }
      await fetchClientContacts(editingClient.client_id);
      await fetchClients();
      setEditingContact(null);
    } catch (error) {
      console.error('Failed to submit contact:', error);
    }
  };

  const handleSetPrimaryContact = async (clientId, contactId) => {
    try {
      await api.put(`/clients/${clientId}/primary-contact/${contactId}/`);
      await fetchClientContacts(clientId);
      await fetchClients();
    } catch (error) {
      console.error('Failed to set primary contact:', error);
    }
  };

  const showContactModal = (contact = null) => {
    setEditingContact(contact || {
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      notes: ''
    });
    setIsContactModalOpen(true);
  };

  const handleCopyToClipboard = async (text, fieldName, event) => {
    try {
      await navigator.clipboard.writeText(text);
      const rect = event.currentTarget.getBoundingClientRect();
      setCopiedTooltip({
        show: true,
        text: 'Copied!',
        x: rect.left + rect.width / 2,
        y: rect.top - 20
      });
      setTimeout(() => setCopiedTooltip({ show: false, text: '', x: 0, y: 0 }), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const filteredContacts = useMemo(() => {
    const searchValue = searchInputValue.toLowerCase();
    let filtered = clientContacts;
    
    // Apply search filter
    if (searchValue) {
      filtered = filtered.filter(contact => 
        contact.first_name?.toLowerCase().includes(searchValue) ||
        contact.last_name?.toLowerCase().includes(searchValue) ||
        contact.email?.toLowerCase().includes(searchValue) ||
        contact.phone?.toLowerCase().includes(searchValue) ||
        contact.notes?.toLowerCase().includes(searchValue)
      );
    }
    
    // Apply type filter
    if (contactsFilter === 'primary') {
      filtered = filtered.filter(contact => contact.is_primary);
    }
    
    return filtered;
  }, [clientContacts, searchInputValue, contactsFilter]);

  const columns = useMemo(
    () => [
      {
        id: 'clientName',
        header: ({ column }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              '&:hover': { color: '#034832' }
            }}
            onClick={column.getToggleSortingHandler()}
          >
            Client Name
            <span style={{ opacity: column.getIsSorted() ? 1 : 0.5 }}>
              {column.getIsSorted() === 'asc' ? <ArrowUp size={14} /> : 
               column.getIsSorted() === 'desc' ? <ArrowDown size={14} /> : 
               <ArrowUpDown size={14} />}
            </span>
          </Box>
        ),
        accessorKey: 'client_name',
        cell: ({ row }) => (
          <div className={styles.clientNameCell}>
            {row.original.client_name}
            {expandedClientId === row.original.client_id && (
              <ChevronUp size={16} className={styles.expandIcon} />
            )}
            {expandedClientId !== row.original.client_id && (
              <ChevronDown size={16} className={styles.expandIcon} />
            )}
          </div>
        ),
      },
      {
        id: 'contact',
        header: ({ column }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              '&:hover': { color: '#034832' }
            }}
            onClick={column.getToggleSortingHandler()}
          >
            Contact Information
            <span style={{ opacity: column.getIsSorted() ? 1 : 0.5 }}>
              {column.getIsSorted() === 'asc' ? <ArrowUp size={14} /> : 
               column.getIsSorted() === 'desc' ? <ArrowDown size={14} /> : 
               <ArrowUpDown size={14} />}
            </span>
          </Box>
        ),
        accessorFn: (row) => `${row.primary_contact?.first_name || ''} ${row.primary_contact?.last_name || ''}`,
        cell: ({ row }) => (
          <div className={styles.contactCell}>
            {row.original.primary_contact ? (
              <>
                <div className={styles.contactName}>
                  {row.original.primary_contact.first_name} {row.original.primary_contact.last_name}
                  <span className={styles.primaryContact}>Primary</span>
                </div>
                <div 
                  className={styles.contactInfo}
                  onClick={(e) => handleCopyToClipboard(row.original.primary_contact.email, 'email', e)}
                >
                  <Mail size={14} />
                  {row.original.primary_contact.email}
                </div>
                <div 
                  className={styles.contactInfo}
                  onClick={(e) => handleCopyToClipboard(row.original.primary_contact.phone, 'phone', e)}
                >
                  <Phone size={14} />
                  {row.original.primary_contact.phone}
                </div>
              </>
            ) : (
              <div className={styles.noContact}>No primary contact</div>
            )}
          </div>
        ),
      },
      {
        id: 'metadata',
        header: () => (
          <Box sx={{ 
            height: '100%',
            display: 'flex',
            alignItems: 'center'
          }}>
            Info
          </Box>
        ),
        cell: ({ row }) => {
          const contactsCount = row.original.contacts?.length || 0;
          const sitesCount = row.original.sites?.length || 0;
          
          return (
            <div className={styles.metadataCell}>
              <span className={styles.metadataBadge}>
                <Users size={14} />
                {contactsCount} contact{contactsCount !== 1 ? 's' : ''}
              </span>
              <span className={styles.metadataBadge}>
                <Building size={14} />
                {sitesCount} site{sitesCount !== 1 ? 's' : ''}
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: () => (
          <Box sx={{ 
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            Actions
          </Box>
        ),
        cell: ({ row }) => (
          <Box className={styles.actionsCell}>
            <Tooltip title="Delete client" arrow>
              <IconButton 
                className={`${styles.actionButton} ${styles.danger}`}
                onClick={(event) => {
                  event.stopPropagation(); // Prevent row expansion when clicking delete
                  setEditingClient(row.original);
                  setDeletePopoverId(event.currentTarget);
                }}
              >
                <Trash2 size={18} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [expandedClientId]
  );

  const table = useReactTable({
    columns,
    data: filteredClients,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  const renderMobileView = () => (
    <Box>
      {filteredClients.map((client) => (
        <Paper key={client.client_id} elevation={1} sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1">{client.client_name}</Typography>
          <Typography variant="body2">{`${client.contact_first_name} ${client.contact_last_name}`}</Typography>
          <Typography variant="body2">{client.contact_phone}</Typography>
          <Typography variant="body2">{client.contact_email}</Typography>
          <Box sx={{ mt: 1 }}>
            <IconButton onClick={() => showModal(client)} color="primary" size="small">
              <Edit />
            </IconButton>
            <IconButton onClick={(event) => setDeletePopoverId(event.currentTarget)} color="error" size="small">
              <Trash2 />
            </IconButton>
          </Box>
        </Paper>
      ))}
    </Box>
  );

  // Function to fetch site-contact assignments for a specific contact
  const fetchSiteContactAssignments = async (contactId) => {
    if (!contactId) return;
    
    try {
      const assignments = {};
      
      // For each site, check if this contact is assigned
      await Promise.all(clientSites.map(async (site) => {
        const { data: siteContacts } = await api.get(`/sites/${site.site_id}/contacts/`);
        const isAssigned = siteContacts.some(c => c.contact_id === contactId && c.is_assigned);
        assignments[site.site_id] = isAssigned;
      }));
      
      setSiteContactAssignments(assignments);
    } catch (error) {
      console.error('Failed to fetch site contact assignments:', error);
    }
  };

  // New function to fetch all site contact assignments for the matrix view
  const fetchSiteContactMatrix = async () => {
    if (!clientSites.length || !clientContacts.length) return;
    
    try {
      const matrix = {};
      
      await Promise.all(clientSites.map(async (site) => {
        matrix[site.site_id] = {};
        const { data: siteContacts } = await api.get(`/sites/${site.site_id}/contacts/`);
        
        clientContacts.forEach(contact => {
          matrix[site.site_id][contact.contact_id] = siteContacts.some(
            c => c.contact_id === contact.contact_id && c.is_assigned
          );
        });
      }));
      
      setSiteContactMatrix(matrix);
    } catch (error) {
      console.error('Failed to fetch site contact matrix:', error);
    }
  };

  // Update contactTabChange to fetch matrix data when switching tabs
  const handleContactTabChange = (event, newValue) => {
    setContactTabValue(newValue);
    
    // If switching to site assignments tab, fetch matrix data
    if (newValue === 1) {
      fetchSiteContactMatrix();
    }
  };

  // Update the toggleSiteAssignment function to also update matrix data
  const toggleSiteAssignment = async (siteId, contactId, isAssigned) => {
    try {
      // Get current assignments for this site
      const { data: siteContacts } = await api.get(`/sites/${siteId}/contacts/`);
      
      // Create new list of contact IDs to assign to this site
      let contactIds = siteContacts
        .filter(c => c.is_assigned)
        .map(c => c.contact_id);
      
      if (isAssigned) {
        // Add this contact
        if (!contactIds.includes(contactId)) {
          contactIds.push(contactId);
        }
      } else {
        // Remove this contact
        contactIds = contactIds.filter(id => id !== contactId);
      }
      
      // Update site contacts
      await api.post(`/sites/${siteId}/contacts/`, {
        contact_ids: contactIds
      });
      
      // Update local state
      setSiteContactAssignments(prev => ({
        ...prev,
        [siteId]: isAssigned
      }));
      
      // Update matrix state
      setSiteContactMatrix(prev => ({
        ...prev,
        [siteId]: {
          ...prev[siteId],
          [contactId]: isAssigned
        }
      }));
      
    } catch (error) {
      console.error('Failed to update site contact assignment:', error);
    }
  };

  const handleContactModalOpen = (client) => {
    setEditingClient(client);
    fetchClientContacts(client.client_id);
    fetchClientSites(client.client_id, true);
    setIsContactModalOpen(true);
  };

  const handleOpenSiteAssignmentModal = (contact) => {
    setSelectedContactForSites(contact);
    fetchSiteContactAssignments(contact.contact_id);
    setIsSiteAssignmentModalOpen(true);
  };

  const handleCloseSiteAssignmentModal = () => {
    setIsSiteAssignmentModalOpen(false);
    setSelectedContactForSites(null);
  };

  // Toggle expanded state for a client row
  const toggleExpand = (clientId, event) => {
    // Prevent triggering when clicking action buttons
    if (event.target.closest(`.${styles.actionButton}`)) return;
    
    if (expandedClientId === clientId) {
      setExpandedClientId(null);
    } else {
      setExpandedClientId(clientId);
      setExpandedViewTab(0);
      // Fetch necessary data when expanding
      const client = clients.find(c => c.client_id === clientId);
      if (client) {
        fetchClientContacts(clientId);
        fetchClientSites(clientId, true);
      }
    }
  };

  const extractStreetAddress = (fullAddress) => {
    const parts = fullAddress.split(',');
    return parts[0].trim();
  };

  const handleAddressSelect = async (selectedAddress) => {
    const streetAddress = extractStreetAddress(selectedAddress);
    setSiteForm(prev => ({...prev, address: streetAddress}));
    try {
      const geocode = await geocodeByAddress(selectedAddress);
      const { lat, lng } = await getLatLng(geocode[0]);
      setSiteForm(prev => ({
        ...prev, 
        latitude: lat.toFixed(6),
        longitude: lng.toFixed(6)
      }));
    } catch (error) {
      console.error('Error', error);
    }
  };

  const validateNumber = (value) => {
    if (value === '' || value === undefined) {
      return '';
    }
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) {
      return '';
    }
    return value;
  };

  const handleSiteSubmit = async (event) => {
    event.preventDefault();
    
    // Form validation would go here
    
    try {
      const formattedValues = {
        ...siteForm,
        client: editingClient.client_id,
        client_name: editingClient.client_name,
        parking_lots_sqft: parseFloat(siteForm.parking_lots_sqft || 0),
        walkway_sqft: parseFloat(siteForm.walkway_sqft || 0),
        latitude: parseFloat(parseFloat(siteForm.latitude || 0).toFixed(6)),
        longitude: parseFloat(parseFloat(siteForm.longitude || 0).toFixed(6)),
        winter_start_date: siteForm.winter_start_date ? moment(siteForm.winter_start_date).format('YYYY-MM-DD') : null,
        winter_end_date: siteForm.winter_end_date ? moment(siteForm.winter_end_date).format('YYYY-MM-DD') : null,
        contract_expiration_date: siteForm.contract_expiration_date ? moment(siteForm.contract_expiration_date).format('YYYY-MM-DD') : null,
        base_revenue: parseFloat(siteForm.base_revenue || 0),
        monthly_rate: parseFloat(siteForm.monthly_rate || 0),
        additional_revenue_amount: parseFloat(siteForm.additional_revenue_amount || 0),
      };

      await api.post('/sites/', formattedValues);
      alert('Site added successfully');
      setIsSiteModalOpen(false);
      setSiteForm({});
      // Refresh client sites
      fetchClientSites(editingClient.client_id, true);
    } catch (error) {
      console.error('Failed to add site:', error);
      alert('Failed to add site. Please check your input and try again.');
    }
  };

  return (
    <ConfigProvider theme={{ token: { colorLinkHover: "#034832", colorPrimary: "#034832" } }}>
      <CssBaseline />
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <Typography variant="h1" className={styles.title}>
              Clients
            </Typography>
            <Typography variant="body1" className={styles.subtitle}>
              Manage your client database and their associated contacts
            </Typography>
          </div>
          <Button
              variant="contained"
            className={styles.addButton}
              onClick={() => showModal()}
            startIcon={<Building2 size={20} />}
            >
              Add New Client
          </Button>
        </div>

        <div className={styles.searchSection}>
          <TextField
            className={styles.searchBar}
            placeholder="Search clients..."
            onChange={(e) => debouncedSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', position: 'absolute', left: 0, top: 0, bottom: 0, zIndex: 1 }}>
                  <Search size={18} />
                </Box>
              ),
            }}
            fullWidth
            variant="standard"
          />
        </div>

        {isLoading ? (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}>Loading...</div>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className={styles.emptyState}>
            <AlertCircle className={styles.emptyStateIcon} />
            <Typography className={styles.emptyStateText}>
              No clients found. Try adjusting your search or add a new client.
            </Typography>
            <Button
              variant="contained"
              className={styles.addButton}
              onClick={() => showModal()}
              startIcon={<Building2 size={20} />}
            >
              Add New Client
            </Button>
          </div>
        ) : (
          <TableContainer className={styles.tableContainer}>
            <Table className={styles.table}>
              <TableHead className={styles.tableHead}>
                  {table.getHeaderGroups().map(headerGroup => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                      <TableCell key={header.id} className={styles.tableHeadCell}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {table.getRowModel().rows.map(row => (
                  <React.Fragment key={row.id}>
                    <TableRow 
                      className={`${styles.tableRow} ${expandedClientId === row.original.client_id ? styles.expandedRow : ''}`}
                      onClick={(e) => toggleExpand(row.original.client_id, e)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className={styles.tableCell}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                    
                    {/* Expanded view */}
                    {expandedClientId === row.original.client_id && (
                      <TableRow className={styles.expandedContentRow}>
                        <TableCell colSpan={table.getAllColumns().length} className={styles.expandedContentCell}>
                          <div className={styles.expandedContentWrapper}>
                            <Tabs 
                              value={expandedViewTab} 
                              onChange={(e, newValue) => setExpandedViewTab(newValue)}
                              className={styles.expandedTabs}
                              TabIndicatorProps={{
                                style: {
                                  backgroundColor: '#034832',
                                  height: '3px'
                                }
                              }}
                            >
                              <Tab 
                                label="Contacts" 
                                className={styles.expandedMainTab}
                                disableRipple
                              />
                              <Tab 
                                label="Sites" 
                                className={styles.expandedMainTab}
                                disableRipple
                              />
                              <Tab 
                                label="Details" 
                                className={styles.expandedMainTab}
                                disableRipple
                              />
                            </Tabs>
                            
                            {/* Contacts Tab */}
                            {expandedViewTab === 0 && (
                              <div className={styles.expandedTabContent}>
                                <div className={styles.expandedTabHeader}>
                                  <Typography variant="h6" className={styles.tabTitle}>Contacts</Typography>
                                  <Button 
                                    variant="contained"
                                    className={styles.addContactButton}
                                    onClick={() => {
                                      setEditingClient(row.original);
                                      setEditingContact(null);
                                      setIsContactFormOpen(true);
                                    }}
                                    startIcon={<UserPlus size={14} />}
                                  >
                                    Add Contact
                                  </Button>
                                </div>
                                
                                {clientContacts.length === 0 ? (
                                  <div className={styles.emptyContactsContainer}>
                                    <div className={styles.emptyContactsContent}>
                                      <AlertCircle size={36} className={styles.emptyIcon} />
                                      <Typography className={styles.emptyTitle}>No contacts found</Typography>
                                      <Typography className={styles.emptySubtitle}>Add a contact to help manage this client</Typography>
                                      <Button
                                        variant="contained"
                                        className={styles.addEmptyButton}
                                        onClick={() => {
                                          setEditingClient(row.original);
                                          setEditingContact(null);
                                          setIsContactFormOpen(true);
                                        }}
                                        startIcon={<UserPlus size={14} />}
                                      >
                                        Add First Contact
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className={styles.contactsCardContainer}>
                                    {clientContacts.map((contact) => (
                                      <div key={contact.contact_id} className={styles.contactCard}>
                                        <div className={styles.contactCardTop}>
                                          {contact.is_primary && (
                                            <div className={styles.primaryTag}>
                                              <Star size={12} />
                                              <span>Primary</span>
                                            </div>
                                          )}
                                          <div className={styles.contactCardActions}>
                                            {!contact.is_primary && (
                                              <Tooltip title="Set as primary contact" arrow>
                                                <IconButton
                                                  className={styles.contactCardAction}
                                                  onClick={() => handleSetPrimaryContact(row.original.client_id, contact.contact_id)}
                                                  size="small"
                                                >
                                                  <Star size={14} />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                            <Tooltip title="Edit contact" arrow>
                                              <IconButton
                                                className={styles.contactCardAction}
                                                onClick={() => {
                                                  setEditingClient(row.original);
                                                  setEditingContact(contact);
                                                  setIsContactFormOpen(true);
                                                }}
                                                size="small"
                                              >
                                                <Edit size={14} />
                                              </IconButton>
                                            </Tooltip>
                                          </div>
                                        </div>
                                        
                                        <div className={styles.contactCardName}>
                                          {contact.first_name} {contact.last_name}
                                        </div>
                                        
                                        <div className={styles.contactCardInfo}>
                                          <div 
                                            className={styles.contactInfoItem}
                                            onClick={(e) => handleCopyToClipboard(contact.email, 'email', e)}
                                          >
                                            <Mail size={14} className={styles.contactInfoIcon} />
                                            <div className={styles.contactInfoText}>
                                              <div className={styles.contactInfoLabel}>Email</div>
                                              <div className={styles.contactInfoValue}>{contact.email}</div>
                                            </div>
                                            <div className={styles.copyIndicator}>
                                              <Copy size={12} />
                                            </div>
                                          </div>
                                          
                                          <div 
                                            className={styles.contactInfoItem}
                                            onClick={(e) => handleCopyToClipboard(contact.phone, 'phone', e)}
                                          >
                                            <Phone size={14} className={styles.contactInfoIcon} />
                                            <div className={styles.contactInfoText}>
                                              <div className={styles.contactInfoLabel}>Phone</div>
                                              <div className={styles.contactInfoValue}>{contact.phone}</div>
                                            </div>
                                            <div className={styles.copyIndicator}>
                                              <Copy size={12} />
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {contact.notes && (
                                          <div className={styles.contactCardNotes}>
                                            <div className={styles.contactNotesLabel}>Notes</div>
                                            <div className={styles.contactNotesContent}>{contact.notes}</div>
                                          </div>
                                        )}
                                        
                                        <div className={styles.contactCardFooter}>
                                          <Button
                                            variant="text"
                                            size="small"
                                            className={styles.siteAssignButton}
                                            onClick={() => handleOpenSiteAssignmentModal(contact)}
                                            startIcon={<Link2 size={14} />}
                                          >
                                            Manage site assignments
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Sites Tab */}
                            {expandedViewTab === 1 && (
                              <div className={styles.expandedTabContent}>
                                <div className={styles.expandedTabHeader}>
                                  <Typography variant="h6">Sites</Typography>
                                  <Button 
                                    variant="contained"
                                    className={styles.addContactButton}
                                    onClick={() => {
                                      setEditingClient(row.original);
                                      setIsSiteModalOpen(true);
                                    }}
                                    startIcon={<MapPin size={14} />}
                                  >
                                    Add Site
                                  </Button>
                                </div>
                                
                                <Tabs 
                                  value={contactTabValue} 
                                  onChange={handleContactTabChange}
                                  className={styles.siteSubTabs}
                                  TabIndicatorProps={{
                                    style: {
                                      backgroundColor: '#034832',
                                    }
                                  }}
                                >
                                  <Tab 
                                    label="Site List" 
                                    sx={{
                                      color: contactTabValue === 0 ? '#034832' : '#6B7280',
                                      fontWeight: contactTabValue === 0 ? 600 : 400,
                                      '&.Mui-selected': {
                                        color: '#034832',
                                      },
                                    }}
                                  />
                                  <Tab 
                                    label="Site Assignments" 
                                    sx={{
                                      color: contactTabValue === 1 ? '#034832' : '#6B7280',
                                      fontWeight: contactTabValue === 1 ? 600 : 400,
                                      '&.Mui-selected': {
                                        color: '#034832',
                                      },
                                    }}
                                  />
                                </Tabs>
                                
                                {contactTabValue === 0 ? (
                                  // Site list view - simplified without expandable accordion
                                  clientSites.length === 0 ? (
                                    <div className={styles.expandedEmptyState}>
                                      <Building size={48} className={styles.emptyStateIcon} />
                                      <Typography className={styles.emptyStateText}>
                                        No sites found for this client.
                                      </Typography>
                                    </div>
                                  ) : (
                                    <div className={styles.sitesCompactList}>
                                      {clientSites.map((site) => (
                                        <div key={site.site_id} className={styles.siteCompactItem}>
                                          <div className={styles.siteCompactHeader}>
                                            <Typography variant="subtitle1" className={styles.siteCompactName}>
                                              {site.site_name}
                                            </Typography>
                                          </div>
                                          <div className={styles.siteCompactDetails}>
                                            <div className={styles.siteCompactInfo}>
                                              <Globe size={16} className={styles.siteCompactIcon} />
                                              <Typography variant="body2" color="textSecondary">
                                                {site.region}
                                              </Typography>
                                            </div>
                                            <div className={styles.siteCompactInfo}>
                                              <MapPin size={16} className={styles.siteCompactIcon} />
                                              <Typography variant="body2" color="textSecondary">
                                                {site.address}
                                              </Typography>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )
                                ) : (
                                  // Site assignments matrix view - more compact
                                  <div className={styles.siteAssignmentsCompactContainer}>
                                    {clientSitesLoading ? (
                                      <div className={styles.loadingOverlay}>
                                        <div className={styles.loadingSpinner}>Loading sites...</div>
                                      </div>
                                    ) : clientSites.length === 0 ? (
                                      <div className={styles.emptyContacts}>
                                        <Building size={48} className={styles.emptyContactsIcon} />
                                        <Typography className={styles.emptyContactsText}>
                                          No sites found for this client.
                                        </Typography>
                                      </div>
                                    ) : (
                                      <div className={styles.siteContactMatrix}>
                                        <TableContainer className={styles.siteContactTable}>
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell className={`${styles.siteContactHeaderCell} ${styles.siteHeaderCell}`}>Site</TableCell>
                                                {clientContacts.map(contact => (
                                                  <TableCell 
                                                    key={contact.contact_id}
                                                    className={styles.siteContactHeaderCell}
                                                    align="center"
                                                  >
                                                    <div className={styles.contactHeaderCell}>
                                                      <div className={styles.contactHeaderName}>
                                                        {contact.first_name} {contact.last_name}
                                                      </div>
                                                      {contact.is_primary && (
                                                        <span className={styles.contactHeaderBadge}>
                                                          <Star size={12} />
                                                        </span>
                                                      )}
                                                    </div>
                                                  </TableCell>
                                                ))}
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {clientSites.map(site => (
                                                <TableRow key={site.site_id} className={styles.siteContactRow}>
                                                  <TableCell className={styles.siteCell}>
                                                    <div className={styles.siteCellContent}>
                                                      <div className={styles.siteName}>
                                                        {site.site_name}
                                                      </div>
                                                      <div className={styles.siteRegion}>
                                                        {site.region}
                                                      </div>
                                                    </div>
                                                  </TableCell>
                                                  {clientContacts.map(contact => (
                                                    <TableCell 
                                                      key={`${site.site_id}-${contact.contact_id}`}
                                                      align="center"
                                                      className={`${styles.contactAssignmentCell} ${siteContactMatrix[site.site_id]?.[contact.contact_id] ? styles.contactAssignmentCellActive : ''}`}
                                                      onClick={async () => {
                                                        const isCurrentlyAssigned = siteContactMatrix[site.site_id]?.[contact.contact_id] || false;
                                                        await toggleSiteAssignment(
                                                          site.site_id, 
                                                          contact.contact_id, 
                                                          !isCurrentlyAssigned
                                                        );
                                                      }}
                                                    >
                                                      <div className={styles.contactAssignmentCheckboxWrapper}>
                                                        <Checkbox
                                                          className={styles.contactAssignmentCheckbox}
                                                          checked={siteContactMatrix[site.site_id]?.[contact.contact_id] || false}
                                                          disabled={true}
                                                          size="small"
                                                        />
                                                      </div>
                                                    </TableCell>
                                                  ))}
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Details Tab */}
                            {expandedViewTab === 2 && (
                              <div className={styles.expandedTabContent}>
                                <div className={styles.expandedTabHeader}>
                                  <Typography variant="h6">Client Details</Typography>
                                  <Button 
                                    variant="outlined"
                                    className={styles.editButton}
                                    onClick={() => showModal(row.original)}
                                    startIcon={<Edit size={16} />}
                                  >
                                    Edit Client
                                  </Button>
                                </div>
                                
                                <div className={styles.clientDetailsGrid}>
                                  <div className={styles.clientDetailCard}>
                                    <div className={styles.clientDetailLabel}>Client Name</div>
                                    <div className={styles.clientDetailValue}>{row.original.client_name}</div>
                                  </div>
                                  {row.original.primary_contact && (
                                    <>
                                      <div className={styles.clientDetailCard}>
                                        <div className={styles.clientDetailLabel}>Primary Contact</div>
                                        <div className={styles.clientDetailValue}>
                                          {row.original.primary_contact.first_name} {row.original.primary_contact.last_name}
                                        </div>
                                      </div>
                                      <div className={styles.clientDetailCard}>
                                        <div className={styles.clientDetailLabel}>Email</div>
                                        <div className={styles.clientDetailValue}>{row.original.primary_contact.email}</div>
                                      </div>
                                      <div className={styles.clientDetailCard}>
                                        <div className={styles.clientDetailLabel}>Phone</div>
                                        <div className={styles.clientDetailValue}>{row.original.primary_contact.phone}</div>
                                      </div>
                                    </>
                                  )}
                                  <div className={styles.clientDetailCard}>
                                    <div className={styles.clientDetailLabel}>Total Contacts</div>
                                    <div className={styles.clientDetailValue}>{row.original.contacts?.length || 0}</div>
                                  </div>
                                  <div className={styles.clientDetailCard}>
                                    <div className={styles.clientDetailLabel}>Total Sites</div>
                                    <div className={styles.clientDetailValue}>{row.original.sites?.length || 0}</div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Client Modal */}
        <Dialog 
          open={isModalOpen} 
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
          className={styles.modal}
        >
          <DialogTitle className={styles.modalTitle}>
            {isEditMode ? "Edit Client" : "Add New Client"}
          </DialogTitle>
          <DialogContent className={styles.modalContent}>
            <form onSubmit={handleSubmit}>
              <TextField
                className={styles.formField}
                label="Client Name"
                name="client_name"
                value={editingClient?.client_name || ''}
                onChange={(e) => setEditingClient({...editingClient, client_name: e.target.value})}
                required
                margin="normal"
              />
              <TextField
                className={styles.formField}
                label="First Name"
                name="contact_first_name"
                value={editingClient?.contact_first_name || ''}
                onChange={(e) => setEditingClient({...editingClient, contact_first_name: e.target.value})}
                required
                margin="normal"
              />
              <TextField
                className={styles.formField}
                label="Last Name"
                name="contact_last_name"
                value={editingClient?.contact_last_name || ''}
                onChange={(e) => setEditingClient({...editingClient, contact_last_name: e.target.value})}
                required
                margin="normal"
              />
              <TextField
                className={styles.formField}
                label="Phone"
                name="contact_phone"
                value={editingClient?.contact_phone || ''}
                onChange={(e) => setEditingClient({...editingClient, contact_phone: e.target.value})}
                required
                margin="normal"
              />
              <TextField
                className={styles.formField}
                label="Email"
                name="contact_email"
                type="email"
                value={editingClient?.contact_email || ''}
                onChange={(e) => setEditingClient({...editingClient, contact_email: e.target.value})}
                required
                margin="normal"
              />
            </form>
          </DialogContent>
          <DialogActions className={styles.modalActions}>
            <Button onClick={handleCloseModal} className={styles.cancelButton}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} className={styles.submitButton}>
              {isEditMode ? 'Update Client' : 'Add Client'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Sites Modal */}
        <Dialog
          open={isSitesModalOpen}
          onClose={handleCloseSitesModal}
          maxWidth="md"
          fullWidth
          className={styles.sitesModal}
        >
          <DialogTitle className={styles.sitesModalHeader}>
            <Typography className={styles.sitesModalTitle}>Client Sites</Typography>
            <IconButton onClick={handleCloseSitesModal} size="small">
              <X size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              className={styles.sitesSearchBar}
              fullWidth
              placeholder="Search sites..."
              value={searchInputValue}
              onChange={handleSearchInputChange}
              InputProps={{
                startAdornment: <Search size={20} color="#666666" />,
              }}
            />
            <div className={styles.sitesList}>
              {filteredSites.map((site) => (
                <Accordion key={site.site_id} className={styles.siteAccordion}>
                  <AccordionSummary
                    expandIcon={<ChevronDown />}
                    className={styles.siteAccordionSummary}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                          {site.site_name}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="textSecondary">
                            Region: {site.region}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Address: {site.address}
                          </Typography>
                        </>
                      }
                    />
                  </AccordionSummary>
                  <AccordionDetails className={styles.siteAccordionDetails}>
                    <SiteContactsManager 
                      site={site}
                      clientId={editingClient?.client_id}
                      onContactsUpdate={() => fetchClientSites(editingClient?.client_id)}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Contacts Modal */}
        <Dialog
          open={isContactModalOpen}
          onClose={() => {
            setIsContactModalOpen(false);
            setSearchInputValue('');
            setContactsFilter('all');
            setIsContactFormOpen(false);
            setContactTabValue(0);
          }}
          maxWidth="md"
          fullWidth
          className={styles.contactsModal}
        >
          <DialogTitle className={styles.contactsModalHeader}>
            <Typography className={styles.contactsModalTitle}>
              Manage Contacts
            </Typography>
            <Typography className={styles.contactsModalSubtitle}>
              <Building2 size={18} />
              {editingClient?.client_name} • {clientContacts.length} contact{clientContacts.length !== 1 ? 's' : ''}
            </Typography>
            <IconButton 
              onClick={() => setIsContactModalOpen(false)}
              sx={{ 
                position: 'absolute',
                right: 24,
                top: 24,
                color: '#6B7280',
              }}
            >
              <X size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Tabs 
              value={contactTabValue} 
              onChange={handleContactTabChange}
              className={styles.contactsTabs}
              TabIndicatorProps={{
                style: {
                  backgroundColor: '#034832',
                }
              }}
            >
              <Tab 
                label="All Contacts" 
                sx={{
                  color: contactTabValue === 0 ? '#034832' : '#6B7280',
                  fontWeight: contactTabValue === 0 ? 600 : 400,
                  '&.Mui-selected': {
                    color: '#034832',
                  },
                }}
              />
              <Tab 
                label="Site Assignments" 
                sx={{
                  color: contactTabValue === 1 ? '#034832' : '#6B7280',
                  fontWeight: contactTabValue === 1 ? 600 : 400,
                  '&.Mui-selected': {
                    color: '#034832',
                  },
                }}
              />
            </Tabs>

            {contactTabValue === 0 ? (
              <>
                <div className={styles.contactsToolbar}>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <Button
                      variant="contained"
                      className={styles.addButton}
                      onClick={() => {
                        setEditingContact(null);
                        setIsContactFormOpen(true);
                      }}
                      startIcon={<UserPlus size={20} />}
                    >
                      Add Contact
                    </Button>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Button
                        variant={contactsFilter === 'all' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setContactsFilter('all')}
                        sx={{
                          borderRadius: '8px',
                          color: contactsFilter === 'all' ? 'white' : '#6B7280',
                          backgroundColor: contactsFilter === 'all' ? '#034832' : 'transparent',
                          borderColor: contactsFilter === 'all' ? '#034832' : '#E5E7EB',
                          '&:hover': {
                            backgroundColor: contactsFilter === 'all' ? '#023324' : '#F9FAFB',
                            borderColor: contactsFilter === 'all' ? '#023324' : '#034832',
                          }
                        }}
                      >
                        All Contacts
                      </Button>
                      <Button
                        variant={contactsFilter === 'primary' ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setContactsFilter('primary')}
                        startIcon={<Star size={16} />}
                        sx={{
                          borderRadius: '8px',
                          color: contactsFilter === 'primary' ? 'white' : '#6B7280',
                          backgroundColor: contactsFilter === 'primary' ? '#034832' : 'transparent',
                          borderColor: contactsFilter === 'primary' ? '#034832' : '#E5E7EB',
                          '&:hover': {
                            backgroundColor: contactsFilter === 'primary' ? '#023324' : '#F9FAFB',
                            borderColor: contactsFilter === 'primary' ? '#023324' : '#034832',
                          }
                        }}
                      >
                        Primary Only
                      </Button>
                    </div>
                  </div>
                  <div className={styles.contactsSearchBar}>
                    <Search size={20} />
                    <input
                      type="text"
                      placeholder="Search contacts by name, email, or phone..."
                      value={searchInputValue}
                      onChange={handleSearchInputChange}
                    />
                    {searchInputValue && (
                      <IconButton
                        size="small"
                        onClick={() => setSearchInputValue('')}
                        sx={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#9CA3AF',
                          '&:hover': { color: '#111827' }
                        }}
                      >
                        <X size={16} />
                      </IconButton>
                    )}
                  </div>
                </div>

                {clientContacts.length === 0 ? (
                  <div className={styles.emptyContacts}>
                    <AlertCircle size={48} className={styles.emptyContactsIcon} />
                    <Typography className={styles.emptyContactsText}>
                      No contacts found for this client. Add your first contact to get started.
                    </Typography>
                    <Button
                      variant="contained"
                      className={styles.addButton}
                      onClick={() => {
                        setEditingContact(null);
                        setIsContactFormOpen(true);
                      }}
                      startIcon={<UserPlus size={20} />}
                    >
                      Add First Contact
                    </Button>
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className={styles.emptyContacts}>
                    <AlertCircle size={48} className={styles.emptyContactsIcon} />
                    <Typography className={styles.emptyContactsText}>
                      No contacts match your search criteria. Try adjusting your filters or search term.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSearchInputValue('');
                        setContactsFilter('all');
                      }}
                      startIcon={<X size={20} />}
                    >
                      Clear Filters
                    </Button>
                  </div>
                ) : (
                  <List className={styles.contactsList}>
                    {filteredContacts.map((contact) => (
                      <ListItem key={contact.contact_id} className={styles.contactItem}>
                        <div style={{ width: '100%' }}>
                          <div className={styles.contactHeader}>
                            <Typography className={styles.contactName}>
                              {contact.first_name} {contact.last_name}
                              {contact.is_primary && (
                                <span className={styles.primaryBadge}>
                                  <Star size={14} />
                                  Primary Contact
                                </span>
                              )}
                            </Typography>
                            <div className={styles.contactActions}>
                              {!contact.is_primary && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  className={styles.setPrimaryButton}
                                  onClick={() => handleSetPrimaryContact(editingClient.client_id, contact.contact_id)}
                                  startIcon={<Star size={16} />}
                                >
                                  Set as Primary
                                </Button>
                              )}
                              <Tooltip title="Assign to sites" arrow>
                                <IconButton
                                  onClick={() => handleOpenSiteAssignmentModal(contact)}
                                  size="small"
                                  sx={{ 
                                    color: '#6B7280',
                                    '&:hover': { 
                                      color: '#034832',
                                      backgroundColor: 'rgba(3, 72, 50, 0.04)'
                                    }
                                  }}
                                >
                                  <Link2 size={16} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit contact" arrow>
                                <IconButton
                                  onClick={() => {
                                    setEditingContact(contact);
                                    setIsContactFormOpen(true);
                                  }}
                                  size="small"
                                  sx={{ 
                                    color: '#6B7280',
                                    '&:hover': { 
                                      color: '#034832',
                                      backgroundColor: 'rgba(3, 72, 50, 0.04)'
                                    }
                                  }}
                                >
                                  <Edit size={16} />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                          <div className={styles.contactInfo}>
                            <Tooltip title="Click to copy email" arrow>
                              <div 
                                className={styles.contactDetail}
                                onClick={(e) => handleCopyToClipboard(contact.email, 'email', e)}
                              >
                                <Mail size={16} />
                                {contact.email}
                              </div>
                            </Tooltip>
                            <Tooltip title="Click to copy phone" arrow>
                              <div 
                                className={styles.contactDetail}
                                onClick={(e) => handleCopyToClipboard(contact.phone, 'phone', e)}
                              >
                                <Phone size={16} />
                                {contact.phone}
                              </div>
                            </Tooltip>
                            {contact.notes && (
                              <div className={styles.contactNotes}>
                                {contact.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            ) : (
              <div className={styles.siteAssignmentsContainer}>
                <div className={styles.siteAssignmentsHeader}>
                  <Typography className={styles.siteAssignmentsTitle}>
                    Site Contact Assignments
                  </Typography>
                  <Typography className={styles.siteAssignmentsSubtitle}>
                    Manage which contacts are assigned to each site
                  </Typography>
                </div>
                
                {clientSitesLoading ? (
                  <div className={styles.loadingOverlay}>
                    <div className={styles.loadingSpinner}>Loading sites...</div>
                  </div>
                ) : clientSites.length === 0 ? (
                  <div className={styles.emptyContacts}>
                    <Building size={48} className={styles.emptyContactsIcon} />
                    <Typography className={styles.emptyContactsText}>
                      No sites found for this client. You need to add sites before you can assign contacts.
                    </Typography>
                  </div>
                ) : (
                  <div className={styles.siteContactMatrix}>
                    <TableContainer className={styles.siteContactTable}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell className={`${styles.siteContactHeaderCell} ${styles.siteHeaderCell}`}>Site</TableCell>
                            {clientContacts.map(contact => (
                              <TableCell 
                                key={contact.contact_id}
                                className={styles.siteContactHeaderCell}
                                align="center"
                              >
                                <div className={styles.contactHeaderCell}>
                                  <div className={styles.contactHeaderName}>
                                    {contact.first_name} {contact.last_name}
                                  </div>
                                  {contact.is_primary && (
                                    <span className={styles.contactHeaderBadge}>
                                      <Star size={12} />
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clientSites.map(site => (
                            <TableRow key={site.site_id} className={styles.siteContactRow}>
                              <TableCell className={styles.siteCell}>
                                <div className={styles.siteCellContent}>
                                  <div className={styles.siteName}>
                                    {site.site_name}
                                  </div>
                                  <div className={styles.siteRegion}>
                                    {site.region}
                                  </div>
                                </div>
                              </TableCell>
                              {clientContacts.map(contact => (
                                <TableCell 
                                  key={`${site.site_id}-${contact.contact_id}`}
                                  align="center"
                                  className={`${styles.contactAssignmentCell} ${siteContactMatrix[site.site_id]?.[contact.contact_id] ? styles.contactAssignmentCellActive : ''}`}
                                  onClick={async () => {
                                    const isCurrentlyAssigned = siteContactMatrix[site.site_id]?.[contact.contact_id] || false;
                                    await toggleSiteAssignment(
                                      site.site_id, 
                                      contact.contact_id, 
                                      !isCurrentlyAssigned
                                    );
                                  }}
                                >
                                  <div className={styles.contactAssignmentCheckboxWrapper}>
                                    <Checkbox
                                      className={styles.contactAssignmentCheckbox}
                                      checked={siteContactMatrix[site.site_id]?.[contact.contact_id] || false}
                                      disabled={true}
                                      size="small"
                                    />
                                  </div>
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Contact Form Dialog */}
        <Dialog
          open={isContactFormOpen}
          onClose={() => setIsContactFormOpen(false)}
          maxWidth="sm"
          fullWidth
          className={styles.modal}
        >
          <DialogTitle className={styles.modalTitle}>
            {editingContact ? "Edit Contact" : "Add New Contact"}
          </DialogTitle>
          <DialogContent className={styles.modalContent}>
            <form onSubmit={handleContactSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    className={styles.formField}
                    label="First Name"
                    value={editingContact?.first_name || ''}
                    onChange={(e) => setEditingContact({...editingContact, first_name: e.target.value})}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    className={styles.formField}
                    label="Last Name"
                    value={editingContact?.last_name || ''}
                    onChange={(e) => setEditingContact({...editingContact, last_name: e.target.value})}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    className={styles.formField}
                    label="Email"
                    type="email"
                    value={editingContact?.email || ''}
                    onChange={(e) => setEditingContact({...editingContact, email: e.target.value})}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    className={styles.formField}
                    label="Phone"
                    value={editingContact?.phone || ''}
                    onChange={(e) => setEditingContact({...editingContact, phone: e.target.value})}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    className={styles.formField}
                    label="Notes"
                    multiline
                    rows={4}
                    value={editingContact?.notes || ''}
                    onChange={(e) => setEditingContact({...editingContact, notes: e.target.value})}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </form>
          </DialogContent>
          <DialogActions className={styles.modalActions}>
            <Button
              onClick={() => setIsContactFormOpen(false)}
              className={styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              onClick={handleContactSubmit}
              className={styles.submitButton}
            >
              {editingContact ? 'Update Contact' : 'Add Contact'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Site Assignment Modal */}
        <Dialog
          open={isSiteAssignmentModalOpen}
          onClose={handleCloseSiteAssignmentModal}
          maxWidth="sm"
          fullWidth
          className={styles.siteAssignmentModal}
        >
          <DialogTitle className={styles.modalTitle}>
            <Typography component="div" className={styles.siteAssignmentModalTitle}>
              Assign Contact to Sites
            </Typography>
            <Typography component="div" className={styles.siteAssignmentModalSubtitle}>
              <span className={styles.siteAssignmentContactName}>
                {selectedContactForSites?.first_name} {selectedContactForSites?.last_name}
              </span>
              {selectedContactForSites?.is_primary && (
                <span className={styles.siteAssignmentPrimaryBadge}>
                  <Star size={14} />
                  Primary Contact
                </span>
              )}
            </Typography>
            <IconButton 
              onClick={handleCloseSiteAssignmentModal}
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
                color: '#6B7280'
              }}
            >
              <X size={20} />
            </IconButton>
          </DialogTitle>
          <DialogContent className={styles.siteAssignmentModalContent}>
            {clientSitesLoading ? (
              <div className={styles.loadingOverlay}>
                <div className={styles.loadingSpinner}>Loading sites...</div>
              </div>
            ) : clientSites.length === 0 ? (
              <div className={styles.emptyContacts}>
                <Building size={48} className={styles.emptyContactsIcon} />
                <Typography className={styles.emptyContactsText}>
                  No sites found for this client. You need to add sites before you can assign contacts.
                </Typography>
              </div>
            ) : (
              <>
                <div className={styles.siteAssignmentHeader}>
                  <Typography className={styles.siteAssignmentInstructions}>
                    <CheckCircle size={16} className={styles.instructionIcon} />
                    Select the sites where this contact should be assigned
                  </Typography>
                </div>
                <List className={styles.siteAssignmentList}>
                  {clientSites.map((site) => (
                    <ListItem key={site.site_id} className={styles.siteAssignmentItem}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={!!siteContactAssignments[site.site_id]}
                            onChange={(e) => {
                              if (selectedContactForSites) {
                                toggleSiteAssignment(
                                  site.site_id,
                                  selectedContactForSites.contact_id,
                                  e.target.checked
                                );
                              }
                            }}
                            className={styles.siteAssignmentCheckbox}
                          />
                        }
                        label={
                          <div className={styles.siteAssignmentLabel}>
                            <div className={styles.siteAssignmentName}>
                              {site.site_name}
                            </div>
                            <div className={styles.siteAssignmentRegion}>
                              <Globe size={14} className={styles.siteAssignmentIcon} />
                              {site.region}
                            </div>
                            <div className={styles.siteAssignmentAddress}>
                              <MapPin size={14} className={styles.siteAssignmentIcon} />
                              {site.address}
                            </div>
                          </div>
                        }
                        className={styles.siteAssignmentFormControl}
                      />
                    </ListItem>
                  ))}
                </List>
                <div className={styles.siteAssignmentSummary}>
                  <div className={styles.siteAssignmentCount}>
                    {Object.values(siteContactAssignments).filter(Boolean).length} of {clientSites.length} sites selected
                  </div>
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions className={styles.modalActions}>
            <Button
              onClick={handleCloseSiteAssignmentModal}
              className={styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCloseSiteAssignmentModal}
              className={styles.submitButton}
              endIcon={<CheckCircle size={16} />}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Popover */}
        <Popover
          open={Boolean(deletePopoverId)}
          anchorEl={deletePopoverId}
          onClose={() => setDeletePopoverId(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box p={2}>
            <Typography variant="body1" style={{ marginBottom: 16 }}>
              Are you sure you want to delete this client?
            </Typography>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                onClick={() => setDeletePopoverId(null)}
                className={styles.cancelButton}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDelete(editingClient?.client_id)}
                className={styles.submitButton}
                color="error"
              >
                Delete
              </Button>
            </Box>
          </Box>
        </Popover>

        {copiedTooltip.show && (
          <div 
            className={styles.copiedTooltip}
            style={{
              position: 'fixed',
              left: copiedTooltip.x,
              top: copiedTooltip.y,
              transform: 'translate(-50%, -100%)'
            }}
          >
            {copiedTooltip.text}
          </div>
        )}

        {/* Site Form Modal - Clean Design */}
        <Dialog
          open={isSiteModalOpen}
          onClose={() => setIsSiteModalOpen(false)}
          maxWidth="md"
          fullWidth
          className={styles.modal}
        >
          <DialogTitle className={styles.modalTitle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Building2 size={20} style={{ color: '#034832' }} />
              Add New Site for {editingClient?.client_name}
            </div>
            <IconButton 
              onClick={() => setIsSiteModalOpen(false)}
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
                color: '#6B7280',
              }}
            >
              <X size={18} />
            </IconButton>
          </DialogTitle>
          <DialogContent className={styles.modalContent}>
            <form onSubmit={handleSiteSubmit} className={styles.siteForm}>
              <div className={styles.formSection} style={{ border: 'none', borderBottom: 'none', borderTop: 'none', paddingBottom: 0 }}>
                <Typography 
                  variant="subtitle2" 
                  className={styles.sectionTitle}
                  sx={{ 
                    mb: 2, 
                    fontWeight: 600, 
                    color: '#111827',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '15px',
                    '&::after': { display: 'none !important' },
                    '&::before': { display: 'none !important' }
                  }}
                >
                  <Building2 size={16} style={{ color: '#034832' }} />
                  Site Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Site Name"
                      value={siteForm?.site_name || ''}
                      onChange={(e) => setSiteForm({...siteForm, site_name: e.target.value})}
                      required
                      fullWidth
                      size="small"
                      placeholder="Enter site name"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Region"
                      value={siteForm?.region || ''}
                      onChange={(e) => setSiteForm({...siteForm, region: e.target.value})}
                      required
                      fullWidth
                      size="small"
                      placeholder="Enter region"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <div className={styles.formField}>
                      <PlacesAutocomplete
                        value={siteForm?.address || ''}
                        onChange={(value) => setSiteForm({...siteForm, address: value})}
                        onSelect={handleAddressSelect}
                      >
                        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                          <div>
                            <TextField
                              {...getInputProps()}
                              placeholder="Search for address"
                              label="Address"
                              variant="outlined"
                              fullWidth
                              required
                              size="small"
                              InputProps={{
                                startAdornment: <InputAdornment position="start"><MapPin size={16} /></InputAdornment>,
                              }}
                            />
                            <div className={styles.autocompleteDropdown}>
                              {loading && <div style={{padding: '8px 12px', color: '#6B7280'}}>Loading...</div>}
                              {suggestions.map((suggestion) => {
                                const style = {
                                  backgroundColor: suggestion.active ? '#f5f5f5' : '#ffffff',
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #e0e0e0',
                                  fontSize: '14px'
                                };
                                return (
                                  <div
                                    {...getSuggestionItemProps(suggestion, { style })}
                                    key={suggestion.placeId}
                                  >
                                    <span>{suggestion.description}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </PlacesAutocomplete>
                    </div>
                  </Grid>
                </Grid>
              </div>

              <div className={styles.formSection} style={{ border: 'none', borderBottom: 'none', borderTop: 'none', paddingBottom: 0 }}>
                <Typography 
                  variant="subtitle2" 
                  className={styles.sectionTitle}
                  sx={{ 
                    mb: 2, 
                    fontWeight: 600, 
                    color: '#111827',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '15px',
                    '&::after': { display: 'none !important' },
                    '&::before': { display: 'none !important' }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#034832" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 3v18h18"></path>
                    <path d="M22 13H9"></path>
                    <path d="M22 8H7"></path>
                    <path d="M22 18H11"></path>
                  </svg>
                  Site Measurements
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Parking Lots Sqft"
                      type="number"
                      value={siteForm?.parking_lots_sqft || ''}
                      onChange={(e) => setSiteForm({...siteForm, parking_lots_sqft: validateNumber(e.target.value)})}
                      required
                      fullWidth
                      size="small"
                      placeholder="0"
                      InputProps={{ 
                        inputProps: { min: 0 },
                        endAdornment: <InputAdornment position="end">sqft</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Walkway Sqft"
                      type="number"
                      value={siteForm?.walkway_sqft || ''}
                      onChange={(e) => setSiteForm({...siteForm, walkway_sqft: validateNumber(e.target.value)})}
                      required
                      fullWidth
                      size="small"
                      placeholder="0"
                      InputProps={{ 
                        inputProps: { min: 0 },
                        endAdornment: <InputAdornment position="end">sqft</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
              </div>

              <div className={styles.formSection} style={{ border: 'none', borderBottom: 'none', borderTop: 'none', paddingBottom: 0 }}>
                <Typography 
                  variant="subtitle2" 
                  className={styles.sectionTitle}
                  sx={{ 
                    mb: 2, 
                    fontWeight: 600, 
                    color: '#111827',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '15px',
                    '&::after': { display: 'none !important' },
                    '&::before': { display: 'none !important' }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#034832" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                    <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                    <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                  </svg>
                  Contract Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth className={styles.formField} size="small">
                      <InputLabel>Contract Status</InputLabel>
                      <Select
                        value={siteForm?.contract_status || ''}
                        onChange={(e) => setSiteForm({...siteForm, contract_status: e.target.value})}
                        required
                      >
                        <MenuItem value="Estimate">Estimate</MenuItem>
                        <MenuItem value="Unconfirmed">Unconfirmed</MenuItem>
                        <MenuItem value="Confirmed">Confirmed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Base Revenue"
                      type="number"
                      value={siteForm?.base_revenue || ''}
                      onChange={(e) => setSiteForm({...siteForm, base_revenue: validateNumber(e.target.value)})}
                      fullWidth
                      size="small"
                      placeholder="0.00"
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: "0.01" }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={4}>
                    <div className={styles.formField}>
                      <label className={styles.formLabel}>Winter Start Date</label>
                      <TextField
                        type="date"
                        fullWidth
                        size="small"
                        value={siteForm?.winter_start_date ? moment(siteForm.winter_start_date).format('YYYY-MM-DD') : ''}
                        onChange={(e) => setSiteForm({...siteForm, winter_start_date: e.target.value})}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          className: styles.datePickerCompact
                        }}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <div className={styles.formField}>
                      <label className={styles.formLabel}>Winter End Date</label>
                      <TextField
                        type="date"
                        fullWidth
                        size="small"
                        value={siteForm?.winter_end_date ? moment(siteForm.winter_end_date).format('YYYY-MM-DD') : ''}
                        onChange={(e) => setSiteForm({...siteForm, winter_end_date: e.target.value})}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          className: styles.datePickerCompact
                        }}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <div className={styles.formField}>
                      <label className={styles.formLabel}>Contract Expiration</label>
                      <TextField
                        type="date"
                        fullWidth
                        size="small"
                        value={siteForm?.contract_expiration_date ? moment(siteForm.contract_expiration_date).format('YYYY-MM-DD') : ''}
                        onChange={(e) => setSiteForm({...siteForm, contract_expiration_date: e.target.value})}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          className: styles.datePickerCompact
                        }}
                      />
                    </div>
                  </Grid>
                </Grid>
              </div>

              <div className={styles.formSection} style={{ border: 'none', borderBottom: 'none', borderTop: 'none', paddingBottom: 0 }}>
                <Typography 
                  variant="subtitle2" 
                  className={styles.sectionTitle}
                  sx={{ 
                    mb: 2, 
                    fontWeight: 600, 
                    color: '#111827',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '15px',
                    '&::after': { display: 'none !important' },
                    '&::before': { display: 'none !important' }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#034832" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"></path>
                    <path d="M12 18V6"></path>
                  </svg>
                  Additional Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Monthly Rate (No HST)"
                      type="number"
                      value={siteForm?.monthly_rate || ''}
                      onChange={(e) => setSiteForm({...siteForm, monthly_rate: validateNumber(e.target.value)})}
                      fullWidth
                      size="small"
                      placeholder="0.00"
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: "0.01" }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth className={styles.formField} size="small">
                      <InputLabel id="territory-manager-label">Territory Manager</InputLabel>
                      <Select
                        labelId="territory-manager-label"
                        value={siteForm?.assigned_territory_manager || ''}
                        onChange={(e) => setSiteForm({...siteForm, assigned_territory_manager: e.target.value})}
                        label="Territory Manager"
                      >
                        <MenuItem value="">
                          <em>Select Territory Manager</em>
                        </MenuItem>
                        {territoryManagers.map((manager) => (
                          <MenuItem key={manager.name} value={manager.name}>
                            {manager.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Additional Revenue Type"
                      value={siteForm?.additional_revenue_type || ''}
                      onChange={(e) => setSiteForm({...siteForm, additional_revenue_type: e.target.value})}
                      fullWidth
                      size="small"
                      placeholder="Enter revenue type"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={styles.formField}
                      label="Additional Revenue Amount"
                      type="number"
                      value={siteForm?.additional_revenue_amount || ''}
                      onChange={(e) => setSiteForm({...siteForm, additional_revenue_amount: validateNumber(e.target.value)})}
                      fullWidth
                      size="small"
                      placeholder="0.00"
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: "0.01" }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      className={styles.formField}
                      label="Dispatch Notes"
                      multiline
                      rows={2}
                      value={siteForm?.dispatch_notes || ''}
                      onChange={(e) => setSiteForm({...siteForm, dispatch_notes: e.target.value})}
                      fullWidth
                      size="small"
                      placeholder="Enter any additional notes for dispatch"
                    />
                  </Grid>
                </Grid>
              </div>
              
              <input type="hidden" name="latitude" value={siteForm?.latitude || ''} />
              <input type="hidden" name="longitude" value={siteForm?.longitude || ''} />
            </form>
          </DialogContent>
          <DialogActions className={styles.modalActions}>
            <Button 
              onClick={() => setIsSiteModalOpen(false)} 
              className={styles.cancelButton}
            >
              CANCEL
            </Button>
            <Button 
              onClick={handleSiteSubmit} 
              className={styles.submitButton}
            >
              ADD SITE
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ConfigProvider>
  );
};

export default ClientListView;